{% block sellops_settings_page %}
<sw-page class="sellops-settings-page">
    {% block sellops_settings_smart_bar_header %}
    <template #smart-bar-header>
        <sw-button
            class="sellops-settings__back-btn"
            variant="ghost"
            size="small"
            @click="$router.push({ name: 'sellops.upsell.analytics' })"
        >
            <sw-icon
                name="regular-chevron-left-s"
                size="16px"
                class="sellops-settings__back-icon"
            />
            {{ $tc('sellops.general.back') }}
        </sw-button>
        <h2>
            {{ $tc('sellops.settings.title') }}
        </h2>
    </template>
    {% endblock %}

    {% block sellops_settings_smart_bar_actions %}
    <template #smart-bar-actions>
        <sw-button-process
            class="sw-settings-system-config__save-action"
            :isLoading="isLoading"
            :processSuccess="isSaveSuccessful"
            :disabled="isLoading"
            variant="primary"
            @process-finish="onSaveFinish"
            @click="onSave"
        >
            {{ $tc('global.default.save') }}
        </sw-button-process>
    </template>
    {% endblock %}

    {% block sellops_settings_content %}
    <template #content>
        <div class="sellops-settings__content">
            <!-- ── Config form ─────────────────────────────────────────────── -->
            <sw-card :title="$tc('sellops.settings.pluginConfigTitle')">
                <p class="sellops-settings__card-hint">
                    {{ $tc('sellops.settings.configHint') }}
                </p>
                <sw-system-config
                    ref="systemConfig"
                    salesChannelSwitchable
                    domain="SellopsPostPurchaseUpsell.config"
                    @loading-changed="onLoadingChanged"
                />
            </sw-card>
            <!-- ── Supported payment methods (READ-ONLY INFO CARD) ────────────
                 Deliberately a plain card with no inputs, no toggles,
                 no buttons so the merchant cannot mistake it for an
                 editable setting. The names come live from the shop's
                 own payment_method records — translated for the admin's
                 current language via the /info endpoint. -->
            <sw-card
                :title="$tc('sellops.settings.supportedMethodsTitle')"
                class="sellops-settings__card-secondary"
            >
                <template #headerRight>
                    <sw-icon
                        name="regular-lock"
                        size="14px"
                        class="sellops-settings__icon-muted"
                        :title="$tc('sellops.settings.supportedMethodsReadOnly')"
                    />
                </template>
                <p class="sellops-settings__methods-hint">
                    {{ $tc('sellops.settings.supportedMethodsDescription') }}
                </p>
                <div
                    v-if="!supportedMethodsLoaded"
                    class="sellops-settings__methods-loading"
                >
                    {{ $tc('sellops.settings.supportedMethodsLoading') }}
                </div>
                <div
                    v-else
                    class="sellops-settings__methods-list"
                >
                    <div
                        v-for="method in supportedMethods"
                        :key="method.key"
                        class="sellops-settings__method-item"
                    >
                        <sw-icon
                            name="regular-check-circle"
                            color="#37D046"
                            size="16px"
                            class="sellops-settings__method-icon"
                        />
                        <span class="sellops-settings__method-name">
                            {{ method.name || $tc('sellops.settings.supportedMethods.' + method.key) }}
                        </span>
                        <span
                            v-if="!method.configuredInShop"
                            class="sellops-settings__method-badge"
                        >
                            {{ $tc('sellops.settings.supportedMethodsNotConfigured') }}
                        </span>
                        <span
                            v-else-if="!method.activeInShop"
                            class="sellops-settings__method-badge sellops-settings__method-badge--inactive"
                        >
                            {{ $tc('sellops.settings.supportedMethodsInactive') }}
                        </span>
                    </div>
                </div>
                <p class="sellops-settings__methods-note">
                    {{ $tc('sellops.settings.supportedMethodsNote') }}
                </p>
            </sw-card>
            <!-- ── Feature overview ────────────────────────────────────────── -->
            <sw-card
                :title="$tc('sellops.settings.overviewTitle')"
                class="sellops-settings__card-secondary"
            >
                <div class="sellops-settings__features">
                    <div class="sellops-settings__feature-row">
                        <sw-icon
                            name="regular-check-circle"
                            color="#37D046"
                            class="sellops-settings__feature-icon"
                        />
                        <div>
                            <strong class="sellops-settings__feature-title">
                                {{ $tc('sellops.settings.featureInterstitial') }}
                            </strong>
                            <p class="sellops-settings__feature-desc">
                                {{ $tc('sellops.settings.featureInterstitialDesc') }}
                            </p>
                        </div>
                    </div>
                    <div class="sellops-settings__feature-row">
                        <sw-icon
                            name="regular-check-circle"
                            color="#37D046"
                            class="sellops-settings__feature-icon"
                        />
                        <div>
                            <strong class="sellops-settings__feature-title">
                                {{ $tc('sellops.settings.featureCountdown') }}
                            </strong>
                            <p class="sellops-settings__feature-desc">
                                {{ $tc('sellops.settings.featureCountdownDesc') }}
                            </p>
                        </div>
                    </div>
                    <div class="sellops-settings__feature-row">
                        <sw-icon
                            name="regular-check-circle"
                            color="#37D046"
                            class="sellops-settings__feature-icon"
                        />
                        <div>
                            <strong class="sellops-settings__feature-title">
                                {{ $tc('sellops.settings.featureDiscount') }}
                            </strong>
                            <p class="sellops-settings__feature-desc">
                                {{ $tc('sellops.settings.featureDiscountDesc') }}
                            </p>
                        </div>
                    </div>
                    <div class="sellops-settings__feature-row">
                        <sw-icon
                            name="regular-check-circle"
                            color="#37D046"
                            class="sellops-settings__feature-icon"
                        />
                        <div>
                            <strong class="sellops-settings__feature-title">
                                {{ $tc('sellops.settings.featureAnalytics') }}
                            </strong>
                            <p class="sellops-settings__feature-desc">
                                {{ $tc('sellops.settings.featureAnalyticsDesc') }}
                            </p>
                        </div>
                    </div>
                    <!-- Free-shipping threshold refund — usage information for
                         the `applyFreeShippingThresholdRefund` toggle rendered
                         by <sw-system-config> in the card above. -->
                    <div class="sellops-settings__feature-row">
                        <sw-icon
                            name="regular-check-circle"
                            color="#37D046"
                            class="sellops-settings__feature-icon"
                        />
                        <div>
                            <strong class="sellops-settings__feature-title">
                                {{ $tc('sellops.settings.featureShippingRefund') }}
                            </strong>
                            <p class="sellops-settings__feature-desc">
                                {{ $tc('sellops.settings.featureShippingRefundDesc') }}
                            </p>
                        </div>
                    </div>
                </div>
            </sw-card>
        </div>
    </template>
    {% endblock %}
</sw-page>
{% endblock %}